import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, PenTool, ArrowRight, Link2, ShieldCheck, Sparkles, X, Heart, Lock, Zap } from "lucide-react";
import { Button } from "../components/ui/button";

const HERO_BG = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const IMG_POOL = "https://images.unsplash.com/photo-1545987796-200677ee1011?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const IMG_STUDIO = "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const IMG_COMMUNITY = "https://images.unsplash.com/photo-1563461661026-49631dd5d68e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CARD_IMG = "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

function CardMock({ img, name, da, niche, rotate, z, offset, dim, traffic }) {
  return (
    <div
      className="absolute w-[300px] rounded-2xl bg-zinc-900 border border-white/10 overflow-hidden shadow-2xl"
      style={{ transform: `rotate(${rotate}deg) translate(${offset}px, ${offset / 2}px)`, zIndex: z, opacity: dim ? 0.55 : 1 }}
    >
      <div className="relative h-44">
        <img src={img} alt={name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
        <div className="absolute top-3 left-3 bg-zinc-950/80 backdrop-blur px-2.5 py-1 rounded-md font-mono text-xs font-bold text-primary border border-white/10">DA {da}</div>
        <div className="absolute top-3 right-3 bg-emerald-500/90 text-white px-2.5 py-1 rounded-md text-[11px] font-semibold">{niche}</div>
      </div>
      <div className="p-4">
        <div className="font-serif text-lg font-semibold leading-tight">{name}</div>
        <div className="font-mono text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Lock size={10} /> url hidden · {traffic}/mo</div>
        <div className="mt-4 flex items-center gap-3">
          <span className="h-10 w-10 rounded-full border border-white/15 flex items-center justify-center text-destructive"><X size={18} /></span>
          <span className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center"><Heart size={18} fill="currentColor" /></span>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="fixed inset-0 grain z-0" />

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-serif font-bold text-2xl tracking-tight">blinxd<span className="text-primary">.</span></span>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Log in</Link>
            <Link to="/register" data-testid="nav-register-link">
              <Button data-testid="nav-get-started" className="rounded-full bg-primary text-white hover:bg-blue-600 px-5 glow-primary">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 mesh-primary">
        <div className="absolute inset-0 opacity-[0.14] pointer-events-none">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 border border-white/10 bg-white/5 rounded-full px-3 py-1 mb-8">
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <span className="font-mono text-[11px] tracking-wide text-muted-foreground">DA-MATCHED · NICHE-LOCKED</span>
            </div>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="font-serif font-bold tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
              Stop buying<br />backlinks.<br />
              <span className="text-primary">Swap them.</span>
            </motion.h1>
            <p className="mt-7 max-w-lg text-lg text-muted-foreground leading-relaxed">
              A matchmaking pool for SEOs and site owners. Swipe through sites in your niche and
              authority band, match on a mutual like, and let AI write both guest posts.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/register" data-testid="hero-cta">
                <Button size="lg" className="rounded-full bg-primary text-white hover:bg-blue-600 h-12 px-7 glow-primary transition-transform active:scale-95">
                  Start swapping <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <a href="#how">
                <Button size="lg" variant="outline" className="rounded-full border-white/15 bg-white/5 hover:bg-white/10 text-foreground h-12 px-7">See how it works</Button>
              </a>
            </div>
            <div className="mt-12 flex items-center gap-10">
              {[["±10", "DA match band"], ["2×", "AI guest posts"], ["48h", "auto-verify window"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-mono text-3xl font-bold text-foreground">{n}</div>
                  <div className="text-xs text-muted-foreground mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 relative h-[460px] hidden md:block">
            <div className="absolute -top-6 -left-6 w-64 h-64 rounded-full bg-primary/20 blur-3xl" />
            <div className="absolute bottom-0 right-4 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />
            <div className="relative w-full h-full flex items-center justify-center">
              <CardMock img={CARD_IMG} name="HIIT Daily" da={47} niche="Fitness" traffic="22K" rotate={-8} z={1} offset={-40} dim />
              <CardMock img={IMG_POOL} name="Yoga Flow Hub" da={49} niche="Fitness" traffic="41K" rotate={6} z={2} offset={40} dim />
              <CardMock img={CARD_IMG} name="Marathon Mindset" da={44} niche="Fitness" traffic="12.5K" rotate={-2} z={3} offset={0} />
            </div>
          </div>
        </div>
      </section>

      {/* Feature split */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-9 relative overflow-hidden">
            <div className="h-12 w-12 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center"><Scale className="text-primary" size={22} /></div>
            <h3 className="font-serif text-2xl sm:text-3xl font-semibold mt-6">Fair Exchange Guarantee</h3>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              We pull your Domain Authority from Moz and only surface sites in your niche within ±10 DA points —
              no more trading your authority away to weaker domains.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 font-mono text-[11px]">
              {["DA-MATCHED", "NICHE-LOCKED", "NO SPAM"].map((t) => (
                <span key={t} className="rounded-md border border-white/10 bg-white/5 px-3 py-1 text-muted-foreground">{t}</span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-9 relative overflow-hidden">
            <img src={IMG_STUDIO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
            <div className="relative">
              <div className="h-12 w-12 rounded-xl bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center"><PenTool className="text-emerald-400" size={22} /></div>
              <h3 className="font-serif text-2xl sm:text-3xl font-semibold mt-6">AI Bridge Studio</h3>
              <p className="text-muted-foreground mt-3 leading-relaxed">
                On a match, GPT-4o writes a 1,000-word SEO guest post that contextually bridges both niches,
                with your backlink placed in the first 300 words.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 font-mono text-[11px]">
                {["GPT-4o", "CONTEXTUAL BRIDGE", "COPY HTML"].map((t) => (
                  <span key={t} className="rounded-md border border-white/10 bg-white/5 px-3 py-1 text-muted-foreground">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* How it works — bento */}
        <div id="how" className="mt-20 scroll-mt-24">
          <div className="font-mono text-[11px] tracking-[0.2em] text-primary uppercase">How it works</div>
          <h2 className="font-serif font-semibold text-3xl sm:text-4xl tracking-tight mt-3">Four steps to a fair swap</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 rounded-2xl border border-white/10 bg-zinc-900/60 p-8 relative overflow-hidden min-h-[260px] flex flex-col justify-end">
              <img src={IMG_POOL} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
              <div className="relative">
                <span className="font-mono text-xs text-primary">01 · 02</span>
                <h4 className="font-serif font-semibold text-2xl mt-2">Add your site, then swipe the pool</h4>
                <p className="text-muted-foreground mt-2 max-w-md">We fetch your DA + niche from Moz, then show only DA-matched sites in your niche. Like to connect, pass to skip.</p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-7 flex flex-col justify-between">
              <div className="h-11 w-11 rounded-xl bg-primary/15 border border-primary/20 flex items-center justify-center"><Sparkles className="text-primary" size={20} /></div>
              <div>
                <span className="font-mono text-xs text-primary">03</span>
                <h4 className="font-serif font-semibold text-xl mt-1">AI writes both posts</h4>
                <p className="text-sm text-muted-foreground mt-2">One click generates a contextual guest article for each side.</p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-7 flex flex-col justify-between">
              <div className="h-11 w-11 rounded-xl bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center"><ShieldCheck className="text-emerald-400" size={20} /></div>
              <div>
                <span className="font-mono text-xs text-emerald-400">04</span>
                <h4 className="font-serif font-semibold text-xl mt-1">Verify &amp; trade</h4>
                <p className="text-sm text-muted-foreground mt-2">Publish, paste the live URL — we crawl to confirm the backlink.</p>
              </div>
            </div>
            <div className="md:col-span-2 rounded-2xl border border-primary/30 bg-primary/10 p-7 flex items-center gap-5">
              <div className="h-12 w-12 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0"><Link2 className="text-primary" size={22} /></div>
              <div>
                <h4 className="font-serif font-semibold text-xl">Every unverified match expires in 48h</h4>
                <p className="text-sm text-muted-foreground mt-1">No ghosting, no one-sided swaps — the pipeline stays clean automatically.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Guarantee high-contrast block */}
        <div className="mt-20 rounded-3xl bg-primary p-10 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white px-3 py-1 font-mono text-[11px] tracking-wide"><Zap size={13} /> FAIR EXCHANGE GUARANTEE</div>
              <h3 className="font-serif font-semibold text-3xl sm:text-4xl mt-4 text-white leading-tight">Both sides post. Both sides get verified. Or nobody trades.</h3>
            </div>
            <Link to="/register">
              <Button size="lg" className="rounded-full bg-white text-primary hover:bg-white/90 h-12 px-8 active:scale-95 transition-transform font-semibold">Join the pool <ArrowRight className="ml-2" size={18} /></Button>
            </Link>
          </div>
        </div>

        {/* Community */}
        <div className="mt-20 rounded-3xl border border-white/10 overflow-hidden relative">
          <img src={IMG_COMMUNITY} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/85 to-zinc-950/40" />
          <div className="relative p-10 sm:p-14 max-w-xl">
            <h3 className="font-serif font-semibold text-3xl sm:text-4xl tracking-tight">Ready to swap fairly?</h3>
            <p className="text-muted-foreground mt-3">Join free today and start swapping backlinks with same-niche, similar-authority sites.</p>
            <Link to="/register" data-testid="footer-cta">
              <Button size="lg" className="mt-7 rounded-full bg-primary text-white hover:bg-blue-600 h-12 px-8 glow-primary active:scale-95 transition-transform">
                Create your account <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between text-sm text-muted-foreground">
          <span className="font-serif font-bold text-foreground text-lg">blinxd<span className="text-primary">.</span></span>
          <span className="font-mono text-xs">© 2026 · Backlink exchange, done fairly.</span>
        </div>
      </footer>
    </div>
  );
}
